<template>
  <div id="interpolator">
    <div v-if="!farmId" class="iv-no-farm">
      <p>Open the Interpolator from one of your farms.</p>
      <router-link to="/">← Back to your farms</router-link>
    </div>

    <template v-else>
      <PluginHeader
        title="Interpolator"
        subtitle="Smooth a point dataset into a continuous IDW grid"
        icon="fad fa-th"
      />

      <transition name="iv-fade">
        <div v-if="alert.show" class="iv-alert" :class="alert.level">
          {{ alert.message }}
        </div>
      </transition>

      <!-- Options panel -->
      <div class="iv-panel">
        <p class="iv-intro">
          Turn a point dataset into a continuous interpolated grid using
          inverse-distance weighting. The grid is built on the platform's
          universal UTM grid, so it lines up with every other grid.
        </p>

        <!-- Source dataset -->
        <label class="iv-label">Point dataset</label>
        <DatasetPicker
          v-if="!dataset"
          mode="point"
          :farmId="farmId"
          @select="pickDataset"
        />
        <div v-else class="iv-chosen">
          <span>{{ dataset.name }}</span>
          <i class="fas fa-times-circle iv-clear" @click="clearDataset"></i>
        </div>

        <!-- Boundary blocks -->
        <label class="iv-label">
          Boundary blocks ({{ selectedBlocks.length }})
        </label>
        <p class="iv-hint">
          Optional — click blocks on the map to bound the interpolation.
          Recommended for sparse datasets; otherwise a hull of the points
          is used.
        </p>
        <ul v-if="selectedBlocks.length" class="iv-block-list">
          <li v-for="b in selectedBlocks" :key="b.properties.blockId">
            {{ b.properties.name || b.properties.blockName || b.properties.blockId }}
          </li>
        </ul>

        <!-- Header — only numeric headers can be interpolated. Image/Text/
             Select columns are excluded so they can't be picked (which
             produced an all-NaN grid). -->
        <template v-if="dataset && dataset.headers && dataset.headers.length">
          <label class="iv-label">Interpolate by</label>
          <div v-if="numericHeaders.length" class="iv-headers">
            <span
              v-for="h in numericHeaders"
              :key="h"
              class="iv-header-chip"
              :class="{ 'is-active': chosenHeader === h }"
              @click="chosenHeader = h"
            >{{ h }}</span>
          </div>
          <p v-else class="iv-hint">
            This dataset has no numeric headers to interpolate.
          </p>
        </template>

        <!-- Run -->
        <button
          v-if="canRun && status === 'idle'"
          class="iv-btn iv-btn-primary"
          @click="interpolate"
        >Interpolate</button>
        <button
          v-if="status === 'processing'"
          class="iv-btn iv-btn-muted"
          disabled
        ><i class="fas fa-sync fa-spin"></i> Interpolating… this can take a while</button>

        <div v-if="warning" class="iv-warn">{{ warning }}</div>

        <!-- Result -->
        <div v-if="status === 'done'" class="iv-results">
          <p class="iv-success">
            <i class="fas fa-check-circle"></i> Interpolation complete.
          </p>
          <label class="iv-label">New dataset name</label>
          <input v-model="resultName" class="iv-input" type="text" />
          <button
            v-if="!saving"
            class="iv-btn iv-btn-success"
            @click="saveResult"
          >Add to Every.Farm</button>
          <button v-else class="iv-btn iv-btn-muted" disabled>
            <i class="fas fa-sync fa-spin"></i> Adding…
          </button>
        </div>

        <div v-if="status === 'error'" class="iv-err">{{ errorMessage }}</div>

        <button class="iv-btn iv-btn-link" @click="reset">Reset</button>
      </div>

      <!-- Map -->
      <div id="interp-map" class="iv-map"></div>
    </template>
  </div>
</template>

<script>
/* eslint-disable no-console */
import firebase from 'firebase/compat/app'
import DatasetPicker from '../raster-analysis/RasterDatasetPicker.vue'
import PluginHeader from '../_shared/PluginHeader.vue'
import { inferColumnType } from '../figures/services/StatsService'

const turf = require('@turf/turf')
const fb = require('@/firebaseConfig.js')

// Public Mapbox token already used elsewhere in the app (FarmsMap.vue).
const MAPBOX_TOKEN = 'pk.eyJ1IjoibGVyZ3AiLCJhIjoiY2p4bmI1NzNzMGN0MTNjcGx4cjF4eDBtdSJ9.2C0FEHhNZ-jGd7jgIRTrEQ'

export default {
  name: 'Interpolator',
  components: { DatasetPicker, PluginHeader },
  data () {
    return {
      dataset: null,
      chosenHeader: '',
      // Sample of the source points, used to infer which headers are
      // numeric (and therefore interpolatable).
      sampleFeatures: [],
      selectedBlocks: [],
      // idle -> processing -> done | error
      status: 'idle',
      requestId: null,
      downloadPath: null,
      warning: '',
      errorMessage: '',
      resultName: '',
      saving: false,
      alert: { show: false, message: '', level: 'alert-info' }
    }
  },
  computed: {
    farmId () {
      return this.$store.state.farm && this.$store.state.farm.id
    },
    farmCenter () {
      const g = this.$store.state.farm && this.$store.state.farm.geolocation
      if (!g) return [-95, 40]
      const lat = g.latitude != null ? g.latitude : g._latitude
      const lng = g.longitude != null ? g.longitude : g._longitude
      return [lng, lat]
    },
    canRun () {
      return !!(this.dataset && this.chosenHeader)
    },
    // Headers whose sampled values infer as numeric. Falls back to all
    // headers only while the sample is still loading; once sampled, an
    // Image/Text/Select column is filtered out so it can't be chosen.
    numericHeaders () {
      const headers = (this.dataset && this.dataset.headers) || []
      const feats = this.sampleFeatures
      if (!feats.length) return headers
      return headers.filter(h => {
        const values = feats.map(f => f.properties && f.properties[h])
        return inferColumnType(values) === 'number'
      })
    }
  },
  created () {
    this._onBlockClick = (e) => {
      const feature = e.features && e.features[0]
      if (!feature) return
      const id = feature.properties.blockId
      const idx = this.selectedBlocks.findIndex(b => b.properties.blockId === id)
      if (idx >= 0) {
        this.selectedBlocks.splice(idx, 1)
      } else {
        this.selectedBlocks.push(feature)
      }
      this.updateSelectedBlocks()
    }
  },
  mounted () {
    if (this.farmId) this.initMap()
  },
  beforeDestroy () {
    this.stopWatch()
    if (this.map) {
      try { this.map.remove() } catch (e) { /* ignore */ }
    }
  },
  methods: {
    flash (message, level) {
      this.alert = { show: true, message, level: level || 'alert-info' }
      clearTimeout(this._alertTimer)
      this._alertTimer = setTimeout(() => { this.alert.show = false }, 7000)
    },

    // --- map --------------------------------------------------------
    initMap () {
      mapboxgl.accessToken = MAPBOX_TOKEN // eslint-disable-line no-undef
      this.map = new mapboxgl.Map({ // eslint-disable-line no-undef
        container: 'interp-map',
        style: 'mapbox://styles/mapbox/satellite-v9',
        center: this.farmCenter,
        zoom: 15
      })
      this.map.addControl(new mapboxgl.NavigationControl()) // eslint-disable-line no-undef
      this.map.on('load', () => this.addBlocksLayer())
    },
    addBlocksLayer () {
      const m = this.map
      const blocks = this.$store.state.blocksGeoJson
      if (!blocks || !blocks.features || !blocks.features.length) return
      m.addSource('interpBlocks', { type: 'geojson', data: blocks })
      m.addLayer({
        id: 'interpBlocksFill',
        type: 'fill',
        source: 'interpBlocks',
        paint: { 'fill-color': '#6e59c7', 'fill-opacity': 0.25 }
      })
      m.addLayer({
        id: 'interpBlocksLine',
        type: 'line',
        source: 'interpBlocks',
        paint: { 'line-color': '#ffffff', 'line-width': 1.5 }
      })
      m.on('click', 'interpBlocksFill', this._onBlockClick)
      m.on('mouseenter', 'interpBlocksFill', this.cursorPointer)
      m.on('mouseleave', 'interpBlocksFill', this.cursorClear)
      try {
        m.fitBounds(turf.bbox(blocks), { padding: 50, animate: false })
      } catch (e) { /* ignore */ }
    },
    cursorPointer () { this.map.getCanvas().style.cursor = 'pointer' },
    cursorClear () { this.map.getCanvas().style.cursor = '' },
    updateSelectedBlocks () {
      const m = this.map
      if (!m) return
      const fc = { type: 'FeatureCollection', features: this.selectedBlocks }
      const src = m.getSource('interpSelected')
      if (src) {
        src.setData(fc)
        return
      }
      m.addSource('interpSelected', { type: 'geojson', data: fc })
      m.addLayer({
        id: 'interpSelectedFill',
        type: 'fill',
        source: 'interpSelected',
        paint: { 'fill-color': '#ffc107', 'fill-opacity': 0.55 }
      })
    },

    // --- source dataset --------------------------------------------
    async pickDataset (dataset) {
      this.dataset = dataset
      this.chosenHeader = ''
      this.sampleFeatures = []
      try {
        await this.mapPoints()
      } catch (err) {
        this.flash('Could not display the points: ' + (err.message || err), 'alert-warning')
      }
    },
    async mapPoints () {
      const m = this.map
      this.removePoints()
      const url = await firebase.storage().ref(this.dataset.file.geoJsonPath).getDownloadURL()
      m.addSource('interpPointsSrc', { type: 'geojson', data: url })
      // Pull a sample of features to infer header types (numeric vs. not)
      // for the "Interpolate by" picker. Non-fatal if it fails — the
      // backend rejects non-numeric headers as a safety net.
      try {
        const resp = await fetch(url)
        const gj = await resp.json()
        this.sampleFeatures = (gj.features || []).slice(0, 200)
      } catch (e) { /* leave sampleFeatures empty; picker falls back to all */ }
      m.addLayer({
        id: 'interpPointsOutline',
        type: 'circle',
        source: 'interpPointsSrc',
        paint: { 'circle-radius': 4, 'circle-color': '#ffffff' }
      })
      m.addLayer({
        id: 'interpPointsDot',
        type: 'circle',
        source: 'interpPointsSrc',
        paint: { 'circle-radius': 2.5, 'circle-color': '#e74c3c' }
      })
    },
    removePoints () {
      const m = this.map
      if (!m) return
      if (m.getLayer('interpPointsDot')) m.removeLayer('interpPointsDot')
      if (m.getLayer('interpPointsOutline')) m.removeLayer('interpPointsOutline')
      if (m.getSource('interpPointsSrc')) m.removeSource('interpPointsSrc')
    },
    clearDataset () {
      this.dataset = null
      this.chosenHeader = ''
      this.sampleFeatures = []
      this.removePoints()
    },

    // --- interpolation request -------------------------------------
    async interpolate () {
      this.status = 'processing'
      this.warning = ''
      this.errorMessage = ''
      try {
        const uid = this.$store.state.currentUser && this.$store.state.currentUser.uid
        const ref = await fb.interpolationRequestsCollection.add({
          farmId: this.farmId,
          datasetId: this.dataset.id,
          geoJsonPath: this.dataset.file.geoJsonPath,
          chosenHeader: this.chosenHeader,
          resolution: 3,
          selectedBlocks: this.selectedBlocks.map(b => ({
            geometry: JSON.stringify(b.geometry),
            properties: {
              name: b.properties.name || b.properties.blockName || '',
              blockId: b.properties.blockId
            }
          })),
          filters: (this.dataset.vizSettings && this.dataset.vizSettings.filters) || [],
          createdBy: uid,
          createdOn: new Date(),
          interpolated: false
        })
        this.requestId = ref.id
        this.resultName = 'Interpolated — ' + this.dataset.name
        this.watchRequest(ref.id)
      } catch (err) {
        this.status = 'error'
        this.errorMessage = 'Could not start interpolation: ' + (err.message || err)
      }
    },
    watchRequest (id) {
      this.stopWatch()
      this._unwatch = fb.interpolationRequestsCollection.doc(id)
        .onSnapshot(snap => {
          const d = snap.data()
          if (!d) return
          if (d.warning) this.warning = d.warning
          if (d.error) {
            this.status = 'error'
            this.errorMessage = d.error
            this.stopWatch()
          } else if (d.interpolated && d.downloadPath) {
            this.downloadPath = d.downloadPath
            this.status = 'done'
            this.stopWatch()
          }
        }, err => {
          this.status = 'error'
          this.errorMessage = 'Lost contact with the interpolation job: ' +
            (err.message || err)
        })
    },
    stopWatch () {
      if (this._unwatch) {
        this._unwatch()
        this._unwatch = null
      }
    },

    // --- save the result -------------------------------------------
    async saveResult () {
      this.saving = true
      try {
        const uid = this.$store.state.currentUser && this.$store.state.currentUser.uid
        // The result GeoJSON was written to downloadPath by the Cloud
        // Function — reference it straight into a new dataset doc, the
        // same shape BulkUploadModal.createDatasetDocument writes.
        await fb.datasetsCollection.add({
          name: this.resultName || ('Interpolated — ' + this.dataset.name),
          description: `IDW interpolation of "${this.dataset.name}" ` +
            `by ${this.chosenHeader}.`,
          headers: [this.chosenHeader],
          farmId: this.farmId,
          createdOn: new Date(),
          createdBy: uid,
          updatedOn: new Date(),
          updatedBy: uid,
          file: {
            geoJsonPath: this.downloadPath,
            path: this.downloadPath,
            originalFileName: 'interpolated.geojson'
          },
          // Drop the result into the same folder as the source dataset (falls
          // back to root) rather than always root, so an interpolation of a
          // dataset that lives in a sub-folder stays alongside it.
          folderId: (this.dataset && this.dataset.folderId) || 'root',
          dynamic: false,
          vizSettings: { colorBy: 'valueBased', colorHeader: this.chosenHeader }
        })
        this.flash('Saved to your farm as a new dataset.', 'alert-success')
      } catch (err) {
        this.flash(
          'Could not save: ' + (err.message || err),
          'alert-danger'
        )
      } finally {
        this.saving = false
      }
    },
    reset () {
      this.stopWatch()
      this.removePoints()
      const m = this.map
      if (m && m.getLayer('interpSelectedFill')) {
        m.removeLayer('interpSelectedFill')
        m.removeSource('interpSelected')
      }
      this.dataset = null
      this.chosenHeader = ''
      this.sampleFeatures = []
      this.selectedBlocks = []
      this.status = 'idle'
      this.requestId = null
      this.downloadPath = null
      this.warning = ''
      this.errorMessage = ''
    }
  }
}
</script>

<style scoped>
#interpolator {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}
.iv-no-farm { padding: 60px 24px; text-align: center; }
/* Floats below the shared 56px plugin header. */
.iv-alert {
  position: fixed;
  top: 68px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 101;
  max-width: 520px;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 0.85rem;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
}
.iv-alert.alert-info { background: #d1ecf1; color: #0c5460; }
.iv-alert.alert-success { background: #d4edda; color: #155724; }
.iv-alert.alert-warning { background: #fff3cd; color: #856404; }
.iv-alert.alert-danger { background: #f8d7da; color: #721c24; }
.iv-fade-enter-active, .iv-fade-leave-active { transition: opacity 0.3s; }
.iv-fade-enter, .iv-fade-leave-to { opacity: 0; }

.iv-panel {
  position: absolute;
  top: 56px;
  left: 0;
  width: 340px;
  height: calc(100% - 56px);
  overflow-y: auto;
  background: #fff;
  border-right: 1px solid #e3e6ea;
  padding: 16px 16px 48px;
  z-index: 1;
}
.iv-title { font-size: 1.15rem; margin: 0 0 6px; color: #2c3e50; }
.iv-title i { color: #066739; }
.iv-intro { font-size: 0.82rem; color: #6c757d; margin-bottom: 14px; }
.iv-label {
  display: block;
  font-size: 0.78rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6c757d;
  margin: 14px 0 4px;
}
.iv-hint { font-size: 0.76rem; color: #889; margin: 0 0 6px; }
.iv-chosen {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  background: rgba(6, 103, 57, 0.10);
  border: 1px solid rgba(6, 103, 57, 0.30);
  border-radius: 6px;
  padding: 8px 10px;
  font-size: 0.88rem;
  color: #2c3e50;
}
.iv-clear { color: #95a5a6; cursor: pointer; }
.iv-clear:hover { color: #c0392b; }
.iv-block-list {
  margin: 0;
  padding-left: 18px;
  font-size: 0.82rem;
  color: #2c3e50;
}
.iv-headers { display: flex; flex-wrap: wrap; gap: 4px; }
.iv-header-chip {
  font-size: 0.8rem;
  padding: 3px 9px;
  border-radius: 12px;
  border: 1px solid #d6dbe0;
  cursor: pointer;
  color: #2c3e50;
}
.iv-header-chip:hover { background: #f2f5f3; }
.iv-header-chip.is-active {
  background: #066739;
  border-color: #066739;
  color: #fff;
}
.iv-input {
  width: 100%;
  font-size: 0.85rem;
  padding: 5px 7px;
  border: 1px solid #d6dbe0;
  border-radius: 4px;
}
.iv-btn {
  width: 100%;
  margin-top: 10px;
  padding: 8px 12px;
  border: 1px solid #d6dbe0;
  border-radius: 6px;
  background: #fff;
  font-size: 0.88rem;
  cursor: pointer;
}
.iv-btn:hover { background: #f5f7f8; }
.iv-btn-primary { background: #066739; border-color: #066739; color: #fff; }
.iv-btn-primary:hover { background: #0a7d46; }
.iv-btn-success { background: #28a745; border-color: #28a745; color: #fff; }
.iv-btn-success:hover { background: #2fbe4f; }
.iv-btn-muted { background: #f0f1f3; color: #95a5a6; cursor: default; }
.iv-btn-link { border: none; background: none; color: #95a5a6; font-size: 0.8rem; }
.iv-results {
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid #eef1f4;
}
.iv-success { font-size: 0.85rem; color: #155724; margin-bottom: 4px; }
.iv-warn {
  margin-top: 10px;
  font-size: 0.8rem;
  color: #856404;
  background: #fff3cd;
  border-radius: 4px;
  padding: 6px 8px;
}
.iv-err { margin-top: 10px; font-size: 0.82rem; color: #c0392b; }

.iv-map {
  position: absolute;
  top: 56px;
  left: 340px;
  width: calc(100% - 340px);
  height: calc(100% - 56px);
}
</style>
